import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { EstadoEstabelecimento, Estabelecimento } from '../tipos';
import { limparDados } from './fatiaPainelControle';

/**
 * Fatia de estabelecimentos do Redux
 * Requisitos: 2.1, 2.2, 2.3, 2.5, 3.2, 3.6
 *
 * - Lista de estabelecimentos e estabelecimento ativo
 * - Seleção automática com prioridade: último selecionado (localStorage) > primeiro da lista
 * - Alternância de estabelecimento com validação de vínculo e limpeza de dados
 * - Persistência do último estabelecimento selecionado no localStorage
 */

const CHAVE_ULTIMO_ESTABELECIMENTO = 'nexo_ultimo_estabelecimento_id';

// ============================================================
// Utilitários de localStorage
// ============================================================

const carregarUltimoEstabelecimentoId = (): string | null => {
  try {
    return localStorage.getItem(CHAVE_ULTIMO_ESTABELECIMENTO);
  } catch {
    return null;
  }
};

const salvarUltimoEstabelecimentoId = (id: string): void => {
  try {
    localStorage.setItem(CHAVE_ULTIMO_ESTABELECIMENTO, id);
  } catch {
    // Ignora erros de localStorage (modo privado, quota excedida, etc.)
  }
};

const removerUltimoEstabelecimentoId = (): void => {
  try {
    localStorage.removeItem(CHAVE_ULTIMO_ESTABELECIMENTO);
  } catch {
    // Ignora erros de localStorage
  }
};

// ============================================================
// Async Thunks
// ============================================================

/**
 * Thunk para carregar estabelecimentos e restaurar o último selecionado.
 * Usado na inicialização da aplicação após login.
 */
export const carregarEstabelecimentos = createAsyncThunk<
  { estabelecimentos: Estabelecimento[]; ultimoId: string | null },
  Estabelecimento[],
  { rejectValue: string }
>(
  'estabelecimento/carregarEstabelecimentos',
  async (estabelecimentos, { rejectWithValue }) => {
    try {
      const ultimoId = carregarUltimoEstabelecimentoId();
      return { estabelecimentos, ultimoId };
    } catch (erro) {
      return rejectWithValue('Erro ao carregar estabelecimentos.');
    }
  }
);

/**
 * Thunk para alternar estabelecimento com validação de vínculo e limpeza de dados.
 * Requisitos: 3.2, 3.6
 *
 * 1. Valida que o id está na lista de estabelecimentos do lojista
 * 2. Atualiza o estabelecimento ativo
 * 3. Persiste o novo id no localStorage
 * 4. Limpa dados do painel (vendas, pagamentos, produtos, caixas)
 */
export const alternarEstabelecimento = createAsyncThunk<
  Estabelecimento,
  string,
  { rejectValue: string }
>(
  'estabelecimento/alternarEstabelecimento',
  async (id, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as { estabelecimento: EstadoEstabelecimento };
    const estabelecimento = state.estabelecimento.estabelecimentos.find((e) => e.id === id);

    if (!estabelecimento) {
      return rejectWithValue('Estabelecimento não encontrado na lista de vínculos do lojista.');
    }

    // Persiste no localStorage
    salvarUltimoEstabelecimentoId(id);

    // Limpa dados do painel anterior (vendas, pagamentos, produtos, caixas)
    dispatch(limparDados());

    return estabelecimento;
  }
);

// ============================================================
// Estado Inicial
// ============================================================

const estadoInicial: EstadoEstabelecimento = {
  estabelecimentos: [],
  estabelecimentoAtual: null,
  carregando: false,
  erro: null,
};

// ============================================================
// Slice
// ============================================================

const fatiaEstabelecimento = createSlice({
  name: 'estabelecimento',
  initialState: estadoInicial,
  reducers: {
    /**
     * Seleção automática de estabelecimento após login.
     * Requisitos: 2.1, 2.2, 2.3, 2.5
     *
     * Lógica de prioridade:
     * 1. Se há apenas 1 estabelecimento → seleciona e persiste
     * 2. Se há múltiplos:
     *    a. Verifica localStorage por `nexo_ultimo_estabelecimento_id`
     *    b. Se encontrado e presente na lista → seleciona
     *    c. Caso contrário → seleciona o primeiro da lista e persiste
     * 3. Se a lista está vazia → mantém estabelecimentoAtual como null
     */
    selecionarAutomaticamente(state, action: PayloadAction<Estabelecimento[]>) {
      const estabelecimentos = action.payload;
      state.estabelecimentos = estabelecimentos;

      // Lista vazia: mantém null (Requisito 2.5)
      if (estabelecimentos.length === 0) {
        state.estabelecimentoAtual = null;
        return;
      }

      // Apenas 1 estabelecimento: seleciona e persiste (Requisito 2.1)
      if (estabelecimentos.length === 1) {
        state.estabelecimentoAtual = estabelecimentos[0];
        salvarUltimoEstabelecimentoId(estabelecimentos[0].id);
        return;
      }

      // Múltiplos estabelecimentos (Requisitos 2.2, 2.3)
      const ultimoId = carregarUltimoEstabelecimentoId();
      if (ultimoId) {
        const ultimo = estabelecimentos.find((e) => e.id === ultimoId);
        if (ultimo) {
          // Último selecionado encontrado na lista → seleciona (Requisito 2.2)
          state.estabelecimentoAtual = ultimo;
          return;
        }
      }

      // Sem último salvo ou não encontrado → seleciona o primeiro e persiste (Requisito 2.3)
      state.estabelecimentoAtual = estabelecimentos[0];
      salvarUltimoEstabelecimentoId(estabelecimentos[0].id);
    },

    definirEstabelecimentos(state, action: PayloadAction<Estabelecimento[]>) {
      state.estabelecimentos = action.payload;

      // Restaura o último estabelecimento selecionado do localStorage
      const ultimoId = carregarUltimoEstabelecimentoId();
      if (ultimoId) {
        const ultimo = action.payload.find((e) => e.id === ultimoId);
        if (ultimo) {
          state.estabelecimentoAtual = ultimo;
          return;
        }
      }

      // Se não há estabelecimento salvo ou não foi encontrado, seleciona o primeiro
      if (!state.estabelecimentoAtual && action.payload.length > 0) {
        state.estabelecimentoAtual = action.payload[0];
        salvarUltimoEstabelecimentoId(action.payload[0].id);
      }
    },

    atualizarTokenEstabelecimento(
      state,
      action: PayloadAction<{ idEstabelecimento: string; token: string; expiracao: string }>
    ) {
      const { idEstabelecimento, token, expiracao } = action.payload;
      const estabelecimento = state.estabelecimentos.find((e) => e.id === idEstabelecimento);
      if (estabelecimento) {
        estabelecimento.token = token;
        estabelecimento.tokenExpiracao = expiracao;
      }
      if (state.estabelecimentoAtual?.id === idEstabelecimento) {
        state.estabelecimentoAtual.token = token;
        state.estabelecimentoAtual.tokenExpiracao = expiracao;
      }
    },

    limparEstabelecimentos(state) {
      state.estabelecimentos = [];
      state.estabelecimentoAtual = null;
      state.carregando = false;
      state.erro = null;
      removerUltimoEstabelecimentoId();
    },

    iniciarCarregamento(state) {
      state.carregando = true;
      state.erro = null;
    },

    finalizarCarregamento(state) {
      state.carregando = false;
    },

    definirErro(state, action: PayloadAction<string>) {
      state.carregando = false;
      state.erro = action.payload;
    },
  },
  extraReducers: (builder) => {
    // carregarEstabelecimentos
    builder
      .addCase(carregarEstabelecimentos.pending, (state) => {
        state.carregando = true;
        state.erro = null;
      })
      .addCase(carregarEstabelecimentos.fulfilled, (state, action) => {
        state.carregando = false;
        state.estabelecimentos = action.payload.estabelecimentos;

        const { ultimoId, estabelecimentos } = action.payload;

        // Restaura o último estabelecimento selecionado
        if (ultimoId) {
          const ultimo = estabelecimentos.find((e) => e.id === ultimoId);
          if (ultimo) {
            state.estabelecimentoAtual = ultimo;
            return;
          }
        }

        // Fallback: seleciona o primeiro
        if (estabelecimentos.length > 0) {
          state.estabelecimentoAtual = estabelecimentos[0];
          salvarUltimoEstabelecimentoId(estabelecimentos[0].id);
        }
      })
      .addCase(carregarEstabelecimentos.rejected, (state, action) => {
        state.carregando = false;
        state.erro = action.payload ?? 'Erro ao carregar estabelecimentos.';
      });

    // alternarEstabelecimento
    builder
      .addCase(alternarEstabelecimento.pending, (state) => {
        state.carregando = true;
        state.erro = null;
      })
      .addCase(alternarEstabelecimento.fulfilled, (state, action) => {
        state.carregando = false;
        state.estabelecimentoAtual = action.payload;
        state.erro = null;
      })
      .addCase(alternarEstabelecimento.rejected, (state, action) => {
        state.carregando = false;
        state.erro = action.payload ?? 'Erro ao alternar estabelecimento.';
      });
  },
});

export const {
  selecionarAutomaticamente,
  definirEstabelecimentos,
  atualizarTokenEstabelecimento,
  limparEstabelecimentos,
  iniciarCarregamento,
  finalizarCarregamento,
  definirErro,
} = fatiaEstabelecimento.actions;

export default fatiaEstabelecimento.reducer;
